/* eslint-disable max-len */
import React, { ReactNode, useEffect, useState } from "react";
// import { useRouter } from "next/navigation";

import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import Pie from "@/components/common/pie";
import PaginationComponent from "@/components/PaginationComponent/Pagination.component";
import { useRouter } from "next/router";
import { ROUTES } from "@/utils/constants";
import { useSelector } from "react-redux";
import { RootState } from "@/store/reducers";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface Customer {
  crm_status: string | null;
  customer_name: string;
  email: string;
  phone: string | null;
  total_conversations: number;
  last_conversation: string;
  intent: string;
  quality_score: number;
  assignees: { name: string; avatar_url: string }[];
  customer_id: any;
}

type Column<T> = {
  header: string;
  accessor: keyof T;
  cell?: (row: T) => ReactNode;
  width?: string;
};

export interface Contact {
  id: string;                                   // generated
  name: string;
  email: string;
  phone: string | null;
  conversations: number;
  lastConversationAt: string;
  agent: {
    name: string;
    avatarUrl?: string;
    extraCount?: number;
    allAssignees?: { name: string; avatar_url: string }[]
  };
  status: string | null;                        // keep original crm_status
  intent: "High" | "Moderate" | "Low" | "Neutral";
  qualityScore: number;
  customerId: any;
}

export function getIntentBadge(intent: string | null) {
  /* helper to DRY the JSX */
  const badge = (text: string, textClass: string, bgClass: string) => (

    <div
      className={`inline-flex items-center gap-2 py-1.5 px-3 rounded-lg text-xs font-semibold ${textClass} ${bgClass} bg-opacity-20`}
    >
      {/* coloured dot inherits text colour via currentColor */}
      <span className="h-2 w-2 rounded-full bg-current" />
      {text} {text !== 'Moderate' && 'Intent'}
    </div>
  );

  switch (intent) {
    case "HIGH_INTENT":
    case "HIGH":
      return badge("High", "text-positive-green", "bg-positive-green");

    case "INTERESTED":
    case "MODERATE":
      return badge("Moderate", "text-secondary-orange", "bg-secondary-orange");

    case "LOW_INTENT":
    case "LOW":
    case "NOT_INTERESTED":
      return badge("Low", "text-negative-red", "bg-negative-red");

    case "NEUTRAL":
    case "NEUTRAL_INTENT":
      return badge("Neutral", "text-neutral-orange", "bg-neutral-orange");

    default:
      return <div className="text-muted-foreground">-</div>;
  }
}

function toContacts(data: Customer[]): any[] {
  return data?.map((c, idx) => {
    const [first, ...rest] = c?.assignees;
    return {
      id: String(idx),                
      name: c.customer_name,
      email: c.email,
      phone: c.phone ?? "",
      conversations: c.total_conversations,
      lastConversationAt: c.last_conversation,
      agent: {
        name: first?.name ?? "Unassigned",
        avatarUrl: first?.avatar_url,
        extraCount: rest.length || undefined,
        allAssignees: c?.assignees?.filter(assignee => assignee?.name !== first?.name),
      },
      status: c.crm_status,
      intent: c.intent,
      // intent: mapIntent(c.intent),
      qualityScore: c.quality_score,
      customerId: c.customer_id,
    };
  });
}

interface GenericTableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
}

function GenericTable<T>({
  columns,
  data,
  onRowClick,
}: GenericTableProps<T>) {
  return (
    <div className="overflow-x-auto border rounded-xl border-[#E4E4E7]">
      <Table className="table-fixed">
        <TableHeader>
          <TableRow>
            {columns.map((c) => (
              <TableHead key={String(c.accessor)} className={`${c.width} truncate`}>
                {c.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.map((row) => (
            <TableRow
              key={(row as any).id}
              onClick={() => onRowClick?.(row)}
              className={onRowClick ? "cursor-pointer" : undefined}
            >
              {columns.map((c) => (
                <TableCell key={String(c.accessor)} className={`${c.width} truncate`}>
                  {c.cell ? c.cell(row) : String(row[c.accessor])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function CustomersTable({customers, page, handlePageChange}) {  
  const router = useRouter();
  const { user } = useSelector((s: RootState) => s.common);
  const [data, setData] = useState<Contact[]>([]);

  const columns: Column<Contact>[] = [
    {
      header: "Contact Name",
      accessor: "name",
      cell: (row) => (
        <span className="font-medium text-primary underline">
          {row.name}
        </span>
      ),
      width: "w-[210px]",
    },
    {
      header: "Contact Details",
      accessor: "email",
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.email}</span>
          <span className="text-sm text-muted-foreground">{row.phone}</span>
        </div>
      ),
      width: "w-[210px]",
    },
    {
      header: "Conversations",
      accessor: "conversations",
      cell: (row) => (
          <p className="font-medium underline">{row.conversations} Conversations</p>
      ),
      width: "w-[175px]",
    },
    {
      header: "Last Conversation",
      accessor: "lastConversationAt",
      cell: (row) =>
        new Date(row.lastConversationAt).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "2-digit",
        }),
      width: "w-[175px]",
    },
    {
      header: "Agent",
      accessor: "agent",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <Avatar className="h-6 w-6">
            {row.agent.avatarUrl && (
              <AvatarImage src={row.agent.avatarUrl} alt={row.agent.name} />
            )}
            <AvatarFallback>
              {row.agent.name
                .split(" ")
                .map((s) => s[0])
                .join("")
                .toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span>
            {row.agent.name}
            {row.agent.extraCount && (
              <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{row.agent.extraCount && ` +${row.agent.extraCount}`}</span>
                
                </TooltipTrigger>
                <TooltipContent
                  align="center"
                  side="right"
                  className="bg-black py-1 px-2 rounded-lg text-xs font-bold text-white whitespace-normal"
                >
                  <div>
                    {
                      row.agent.allAssignees?.map(assignee => (
                        <div key={assignee?.name} className="flex items-center gap-2 my-1">
                          <Avatar className="h-6 w-6">
                            {assignee?.avatar_url && (
                              <AvatarImage src={assignee?.avatar_url} alt={assignee?.name} />
                            )}
                            <AvatarFallback>
                              {assignee.name
                                .split(" ")
                                .map((s) => s[0])
                                .join("")
                                .toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <p>{assignee?.name}</p>
                        </div>
                      ))
                    }
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            )}

            
          </span>
        </div>
      ),
      width: "w-[210px]",
    },
    { header: "Status",
      accessor: "status",
      cell: (row) => (
        <p className=''>
          {row.status ?? ''}
        </p>
      ),
      width: "w-[150px]",
     },
    {
      header: "Intent",
      accessor: "intent",
      cell: (row) => getIntentBadge(row.intent),
      width: 'w-[160px]'
      // cell: (row) => (
      //   <Badge className={`gap-1 ${intentColor(row.intent)}`} variant="outline">
      //     {row.intent}
      //   </Badge>
      // ),
    },
    {
      header: "Quality Score",
      accessor: "qualityScore",
      width: "w-[160px]",
      cell: (row) => row?.qualityScore !== null && row?.qualityScore !== undefined && Number.isFinite(Number(row?.qualityScore)) ? <Pie percentage={Number(row?.qualityScore)} /> : '-'
    },
  ];

  useEffect(()=>{
    const mappedContacts = toContacts(customers);
    setData(mappedContacts)
  },[customers])

  return (
    <div className="flex flex-col">

    <GenericTable
      columns={columns}
      data={data}
      onRowClick={(row) => {router.push(`/${(user?.organisation?.feature_flags?.show_new_l2_page ?? false) ? ROUTES.INSIGHTS.L2 : ROUTES.INSIGHTS.DETAILS}/${row?.customerId}`)}}
      />
      <PaginationComponent
        selectedPage={page}
        onPageChange={handlePageChange}
        className="ml-auto mt-3"
        tabClass="bg-[#09090B] text-white"
      />
      </div>
  );
}